import { type Options, RRule, Weekday } from "rrule";
import { z } from "zod";
import { tasksCopy } from "@/features/tasks/copy";
import { sharedCopy } from "@/shared/copy";
import { isTenderCurrency } from "@/shared/money";
import { time } from "@/shared/week";

const refused = tasksCopy.refused;

const title = z
  .string({ error: refused.notATitle })
  .trim()
  .min(1, refused.notATitle)
  .max(500, refused.notATitle);
const notes = z
  .string({ error: refused.notANote })
  .max(10_000, refused.notANote)
  .nullable();
const priority = z
  .number({ error: refused.notAPriority })
  .int(refused.notAPriority)
  .min(0, refused.notAPriority)
  .max(3, refused.notAPriority);
const dueOn = z.iso.date({ error: sharedCopy.refused.notADate }).nullable();
const id = z.string().min(1);

/** Every second weekday of the month is +2TU; a fifth exists in some month, a sixth in none. */
const nth = (day: unknown) =>
  day instanceof Weekday &&
  day.n !== undefined &&
  day.n !== 0 &&
  Math.abs(day.n) <= 5;
const plain = (day: unknown) => day instanceof Weekday && day.n === undefined;
const monthDay = (day: unknown) =>
  Number.isInteger(day) && day !== 0 && Math.abs(day as number) <= 31;

/**
 * Only the rules the spec lists. Anything else is refused, because a rule that can never fall
 * (yearly on 30 February) makes rrule walk to year 9999 on every check-off.
 */
function supported(options: Partial<Options>): boolean {
  const { freq, interval, byweekday, bymonthday, ...rest } = options;
  if (Object.keys(rest).length > 0) return false;
  if (
    interval !== undefined &&
    !(Number.isInteger(interval) && interval >= 1 && interval <= 99)
  ) {
    return false;
  }
  const days =
    byweekday === undefined || byweekday === null ? [] : [byweekday].flat();
  const dates =
    bymonthday === undefined || bymonthday === null ? [] : [bymonthday].flat();
  switch (freq) {
    case RRule.DAILY:
    case RRule.YEARLY:
      return days.length === 0 && dates.length === 0;
    case RRule.WEEKLY:
      return dates.length === 0 && days.every(plain);
    case RRule.MONTHLY:
      // Every 12 months visits one month only; the 30th from the end would never fall if it is
      // February. The 30th itself falls on its last day.
      if ((interval ?? 1) % 12 === 0 && dates.some((day) => day < -28)) {
        return false;
      }
      return (
        days.length + dates.length <= 1 &&
        days.every(nth) &&
        dates.every(monthDay)
      );
    default:
      return false;
  }
}

/** An RFC 5545 rule, stored as rrule writes it back: `RRULE:FREQ=WEEKLY;BYDAY=MO`. */
const recurrence = z
  .string({ error: refused.notARepeat })
  .max(200, refused.notARepeat)
  .transform((value, ctx) => {
    let options: Partial<Options>;
    try {
      options = RRule.parseString(value);
    } catch {
      options = {};
    }
    if (supported(options)) return RRule.optionsToString(options);
    ctx.issues.push({
      code: "custom",
      message: refused.notARepeat,
      input: value,
    });
    return z.NEVER;
  })
  .nullable();
const repeatFrom = z.enum(["due", "completed"]);

const taskStatus = z.enum(["todo", "in_progress", "done"]);

/** No position: a new task goes at the end of its list. */
export const createTaskInput = z.object({
  title,
  notes: notes.optional(),
  status: taskStatus.optional(),
  priority: priority.optional(),
  projectId: id.nullable().optional(),
  parentId: id.nullable().optional(),
  assigneeId: id.nullable().optional(),
  dueOn: dueOn.optional(),
  dueTime: time.nullable().optional(),
  recurrence: recurrence.optional(),
  repeatFrom: repeatFrom.optional(),
});

/** Moving between lists is `moveTaskInput`; handing to Foreday is its own route, taking back is null here. */
export const editTaskInput = z.object({
  delegateId: z.null().optional(),
  title: title.optional(),
  notes: notes.optional(),
  status: taskStatus.optional(),
  priority: priority.optional(),
  assigneeId: id.nullable().optional(),
  dueOn: dueOn.optional(),
  dueTime: time.nullable().optional(),
  recurrence: recurrence.optional(),
  repeatFrom: repeatFrom.optional(),
});

export const editTasksInput = z.object({
  edits: z
    .array(
      editTaskInput.extend({
        id,
        /**
         * A check-off's Undo putting back the place the task had in my order, null where it had
         * none. Taken only on a task of mine, whose place it is; on another's the task starts
         * last, as with any new date.
         */
        myOrderKey: z
          .string()
          .regex(/^[0-9A-Za-z]{1,100}$/)
          .nullable()
          .optional(),
      }),
    )
    .min(1)
    .max(200),
});

/**
 * Where a task goes: a project (or none) and a parent (or none), placed after `afterId` among its
 * new siblings, first when `afterId` is null, last when it is absent.
 */
export const moveTaskInput = z.object({
  projectId: id.nullable(),
  parentId: id.nullable(),
  afterId: id.nullable().optional(),
});

/** A task of my Tasks list dropped just after `afterId` among its day's tasks, or first when null. */
export const orderMyTasksInput = z.object({
  id,
  afterId: id.nullable(),
});

/** One activity line's Undo: the audit rows the line showed. */
export const undoEditInput = z.object({
  ids: z.array(id).min(1).max(50),
});

/** Handing a leaver's open tasks to one person at once. */
export const assignTasksInput = z.object({
  taskIds: z.array(id).min(1).max(500),
  assigneeId: id,
});

const projectName = z
  .string({ error: refused.notAProjectName })
  .trim()
  .min(1, refused.notAProjectName)
  .max(200, refused.notAProjectName);
const projectAccess = z.enum(["company", "private"]);

export const createProjectInput = z.object({
  name: projectName,
  access: projectAccess.optional(),
  leadId: id.nullable().optional(),
  clientId: id.nullable().optional(),
  dueOn: dueOn.optional(),
});

export const editProjectInput = z.object({
  name: projectName.optional(),
  access: projectAccess.optional(),
  leadId: id.nullable().optional(),
  clientId: id.nullable().optional(),
  dueOn: dueOn.optional(),
});

/** A first load reads a list a page at a time, each page starting after the last id it held. */
export const pageSearch = z.object({ after: id.optional() });

/** The draft a client's billing is opened from; deleting the client there leaves it no client. */
export const clientDraftSearch = z.object({ draft: id.optional() });

/** A client is created from the picker with its name alone, and renamed there. */
export const clientInput = z.object({
  name: z
    .string({ error: refused.notAClientName })
    .trim()
    .min(1, refused.notAClientName)
    .max(200, refused.notAClientName),
});

const billingText = (max: number) =>
  z
    .string(refused.tooLong(max))
    .trim()
    .max(max, refused.tooLong(max))
    .transform((value) => value || null)
    .nullable()
    .optional();

/** The Bill to sheet. A field left out keeps its value; null clears it. */
export const clientBillingInput = z.object({
  legalName: billingText(200),
  billingAddress: billingText(500),
  /** ISO 3166-1 alpha-2. */
  country: z
    .string(refused.notACountry)
    .regex(/^[A-Z]{2}$/, refused.notACountry)
    .nullable()
    .optional(),
  taxId: billingText(30),
  billingEmail: z
    .string(refused.notAnEmail)
    .trim()
    .pipe(z.email(refused.notAnEmail).max(254, refused.notAnEmail))
    .nullable()
    .optional(),
  /** ISO 4217. */
  currency: z
    .string(refused.notACurrency)
    .refine(isTenderCurrency, refused.notACurrency)
    .nullable()
    .optional(),
  paymentTermsDays: z
    .int(refused.notTerms)
    .min(0, refused.notTerms)
    .max(365, refused.notTerms)
    .optional(),
});

const commentBody = z
  .string({ error: refused.notAComment })
  .trim()
  .min(1, refused.notAComment)
  .max(10_000, refused.notAComment);

export const commentInput = z.object({ body: commentBody });

/** Images uploaded to the task beforehand, attached as the comment posts. */
export const createCommentInput = commentInput.extend({
  imageIds: z.array(id).max(10, refused.tooManyImages).optional(),
});

/** The task open beside the main panel, on any screen; a bad id opens nothing. */
export const openTaskSearch = z.object({
  task: id.optional().catch(undefined),
});

/** The working set's next page starts after this task id. */
export const workingSetSearch = z.object({ after: id.optional() });

/** Recently deleted's filter, in the address so a link opens the same list. */
export const deletedSearch = z.object({
  show: z
    .enum(["all", "tasks", "projects", "invoices"])
    .catch("all")
    .default("all"),
});

/** Whether Done is open at the foot of Tasks lives in the address. */
export const tasksSearch = z.object({
  done: z.boolean().catch(false).default(false),
});

export const TASKS_SEARCH_DEFAULTS = { done: false } as const;

/** Tasks adds its view: mine, or the tasks I gave someone else (`docs/spec/tasks.md`, Tasks). */
export const tasksViewSearch = tasksSearch.extend({
  view: z.enum(["mine", "given"]).catch("mine").default("mine"),
});

export const TASKS_VIEW_DEFAULTS = {
  ...TASKS_SEARCH_DEFAULTS,
  view: "mine",
} as const;

export const DELETED_SEARCH_DEFAULTS = { show: "all" } as const;

/**
 * A project's or a parent's tasks done before the working set's 14 days, newest first. `before` is
 * the last row's `completedAt` and id, as the previous page's `next` gave them.
 */
export const doneTasksSearch = z
  .object({
    projectId: id.optional(),
    parentId: id.optional(),
    before: z
      .string()
      .regex(/^[^,]+,[^,]+$/)
      .transform((value) => {
        const [at = "", taskId = ""] = value.split(",");
        return { at, taskId };
      })
      .pipe(z.object({ at: z.iso.datetime({ offset: true }), taskId: id }))
      .optional(),
  })
  .refine(
    (search) =>
      (search.projectId === undefined) !== (search.parentId === undefined),
    {
      message: refused.notADoneList,
    },
  );

/**
 * What Foreday and an outside assistant may narrow a task list by. Open tasks when no status or
 * completion day is named; a completion day, in the company's zone, means done.
 */
export const listTasksInput = z.strictObject({
  status: taskStatus.optional(),
  projectId: id.optional(),
  mine: z.boolean().optional(),
  assigneeId: id.optional(),
  completedFrom: z.iso.date().optional(),
  completedTo: z.iso.date().optional(),
});

/** Search everything: every word must appear in the title, the notes or the comment it matches. */
export const searchInput = z.object({
  q: z
    .string({ error: refused.notASearch })
    .trim()
    .min(1, refused.notASearch)
    .max(200, refused.notASearch),
});

/** A task as the working set and the delta read carry it: without its notes. */
export const listedTask = z.object({
  id: z.string(),
  title: z.string(),
  status: taskStatus,
  priority: z.number().int(),
  orderKey: z.string(),
  /** Its place in its person's own Tasks list; null until they drag it there. */
  myOrderKey: z.string().nullable(),
  projectId: z.string().nullable(),
  parentId: z.string().nullable(),
  assigneeId: z.string().nullable(),
  /** Set while the task is handed to Foreday. */
  delegateId: z.string().nullable(),
  createdById: z.string().nullable(),
  /** `yyyy-MM-dd` on the company's calendar. */
  dueOn: z.string().nullable(),
  /** `HH:mm`, wall-clock time in the company's zone. */
  dueTime: z.string().nullable(),
  recurrence: z.string().nullable(),
  repeatFrom: repeatFrom,
  completedAt: z.iso.datetime().nullable(),
  deletedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});

export type DeletedShow = z.infer<typeof deletedSearch>["show"];

/** The projects list's tab, and whether the new project dialog is open, live in the address. */
export const projectsSearch = z.object({
  view: z.enum(["active", "archived"]).default("active"),
  new: z.boolean().default(false),
});

export const PROJECTS_SEARCH_DEFAULTS = { view: "active", new: false } as const;

export type CreateTaskInput = z.infer<typeof createTaskInput>;
export type EditTaskInput = z.infer<typeof editTaskInput>;
export type EditTasksInput = z.infer<typeof editTasksInput>;
export type MoveTaskInput = z.infer<typeof moveTaskInput>;
export type OrderMyTasksInput = z.infer<typeof orderMyTasksInput>;
export type AssignTasksInput = z.infer<typeof assignTasksInput>;
export type CreateProjectInput = z.infer<typeof createProjectInput>;
export type EditProjectInput = z.infer<typeof editProjectInput>;
export type CommentInput = z.infer<typeof commentInput>;
export type ClientBillingInput = z.input<typeof clientBillingInput>;
export type CreateCommentInput = z.infer<typeof createCommentInput>;
export type ClientInput = z.infer<typeof clientInput>;
export type ListTasksInput = z.infer<typeof listTasksInput>;
/** As sent: the repository parses it again for any second caller. */
export type DoneTasksSearch = z.input<typeof doneTasksSearch>;

export type ProjectsSearch = z.infer<typeof projectsSearch>;
export type TasksView = z.infer<typeof tasksViewSearch>["view"];

/** Who a project screen names: the route reads the company's people and hands these over. */
export interface ProjectPerson {
  readonly id: string;
  readonly kind: "person" | "invitation";
  readonly name: string;
  readonly email: string;
  readonly state: "active" | "invited" | "deactivated";
  readonly photoKey: string | null;
}

export type ListedTask = z.infer<typeof listedTask>;
